// Source of truth: shared/MineTrace.Shared/Enums/
// Keep these in sync with the .NET shared library

export type EnumOption = { value: number; label: string }

export function enumLabel(list: readonly EnumOption[], value: number | null | undefined, fallback = '—'): string {
  if (value == null || value === 0) return fallback
  return list.find(item => item.value === value)?.label ?? fallback
}

// ── IgafomDocumentStatus (MineTrace.Shared.Enums.IgafomDocumentStatus) ──
export const IGAFOM_DOCUMENT_STATUSES: readonly EnumOption[] = [
  { value: 5, label: 'Presentado' },
  { value: 1, label: 'En Evaluación' },
  { value: 2, label: 'Aprobado' },
  { value: 3, label: 'Observado' },
  { value: 4, label: 'Rechazado' },
]

// ── IgafomType (MineTrace.Shared.Enums.IgafomType) ──
export const IGAFOM_TYPES: readonly EnumOption[] = [
  { value: 1, label: 'Preventivo' },
  { value: 2, label: 'Correctivo' },
  { value: 3, label: 'Preventivo y Correctivo' },
]

// ── ExplosiveAuthorizationType (MineTrace.Shared.Enums.ExplosiveAuthorizationType) ──
export const EXPLOSIVE_AUTHORIZATION_TYPES: readonly EnumOption[] = [
  { value: 1, label: 'Autorización de Adquisición y Uso' },
  { value: 2, label: 'Autorización de Almacenamiento (polvorín)' },
]

// ── ProductionPeriod (MineTrace.Shared.Enums.ProductionPeriod) ──
export const PRODUCTION_PERIODS: readonly EnumOption[] = [
  { value: 1, label: 'Ene - Jun' },
  { value: 2, label: 'Jul - Dic' },
]

// ── Actividades Economicas ──
export const ECONOMIC_ACTIVITIES = [
  { value: 0, label: 'Seleccionar...' },
  { value: 1, label: 'Minería' },
  { value: 2, label: 'Transporte de carga' },
  { value: 3, label: 'Comercialización de minerales' },
  { value: 4, label: 'Procesamiento de minerales' },
  { value: 5, label: 'Otros' },
]

// ── MiningCategory (MineTrace.Shared.Enums.MiningCategory) ──
export const MINING_CATEGORIES = [
  { value: 0, label: 'Seleccionar...' },
  { value: 1, label: 'Gran Minería' },
  { value: 2, label: 'Mediana Minería' },
  { value: 3, label: 'Pequeño Productor Minero (PPM)' },
  { value: 4, label: 'Productor Minero Artesanal (PMA)' },
]

// ── MineralSubstance (MineTrace.Shared.Enums.MineralSubstance) ──
export const MINERAL_SUBSTANCES = [
  { value: 0, label: 'Seleccionar...' },
  { value: 1, label: 'Metálica (Au, Ag, Cu)' },
  { value: 2, label: 'No Metálica (Calizas, Arcillas)' },
]

// ── FormalizationStatus (MineTrace.Shared.Enums.FormalizationStatus) ──
export const FORMALIZATION_STATUSES = [
  { value: 0, label: 'Seleccionar...' },
  { value: 1, label: 'En Proceso' },
  { value: 2, label: 'Formalizado por vía extraordinaria' },
  { value: 3, label: 'Formalizado por vía ordinaria' },
]

// ── ExtractionMethod (MineTrace.Shared.Enums.ExtractionMethod) ──
export const EXTRACTION_METHODS = [
  { value: 0, label: 'Seleccionar...' },
  { value: 1, label: 'Cielo Abierto' },
  { value: 2, label: 'Subterránea' },
  { value: 3, label: 'Placeres Auríferos' },
]

// ── LegalDocumentType (MineTrace.Shared.Enums.LegalDocumentType) ──
// Otros documentos legales que el minero puede presentar (observación 13).
export const LEGAL_DOCUMENT_TYPES: EnumOption[] = [
  { value: 1, label: 'Certificado de Operación Minera (COM)' },
  { value: 2, label: 'Certificados de Inspección Técnica' },
  { value: 3, label: 'Declaración de Impacto Ambiental (DIA)' },
  { value: 4, label: 'Estudio de Impacto Ambiental Semidetallado (EIAsd)' },
  { value: 5, label: 'Estudio de Impacto Ambiental Detallado (EIAd)' },
  { value: 6, label: 'Informe Técnico Sustentatorio (ITS)' },
  { value: 7, label: 'Programa de Adecuación y Manejo Ambiental (PAMA)' },
  { value: 8, label: 'Plan de Cierre de Minas (PCM)' },
]

// ── ExploitationMethod (MineTrace.Shared.Enums.ExploitationMethod) ──
// Valores 1-3: métodos de explotación · valores 4-8: tipos de procesamiento (planta de beneficio).
export const EXPLOITATION_METHODS: EnumOption[] = [
  { value: 1, label: 'Minería Subterránea' },
  { value: 2, label: 'Minería a Tajo Abierto' },
  { value: 3, label: 'Minería Aluvial (placeres auríferos)' },
]

export const PROCESSING_METHODS: EnumOption[] = [
  { value: 4, label: 'Concentración por Flotación' },
  { value: 5, label: 'Lixiviación y Cianuración' },
  { value: 6, label: 'Extracción por Solventes y Electroobtención' },
  { value: 7, label: 'Concentración Gravimétrica' },
  { value: 8, label: 'Amalgamación con Mercurio' },
]

/** Unión de explotación + procesamiento — para resolver la etiqueta de cualquier método guardado. */
export const ALL_MINING_METHODS: EnumOption[] = [...EXPLOITATION_METHODS, ...PROCESSING_METHODS]

// ── RecpoType (MineTrace.Shared.Enums.RecpoType) - Condicion de RECPO ──
export const RECPO_TYPES = [
  { value: 0, label: 'Seleccionar...' },
  { value: 1, label: 'COMPRA Y VENTA DE ORO' },
  { value: 2, label: 'COMPRA/VENTA Y REFINAMIENTO DE ORO' },
  { value: 3, label: 'SOLO VENTA DE ORO' },
]

// ── BankEntity (MineTrace.Shared.Enums.BankEntity) ──
export const BANK_ENTITIES = [
  { value: 0, label: 'Seleccionar...' },
  { value: 1, label: 'BCP' },
  { value: 2, label: 'BBVA' },
  { value: 3, label: 'Scotiabank' },
  { value: 4, label: 'Interbank' },
  { value: 5, label: 'BanBif' },
  { value: 6, label: 'Banco de la Nacion' },
  { value: 7, label: 'Pichincha' },
  { value: 8, label: 'MiBanco' },
  { value: 99, label: 'Otros' },
]

// ── AccountType (MineTrace.Shared.Enums.AccountType) ──
// Note: Detracción accounts are always issued by Banco de la Nación — the UI auto-fills
// and locks the bank field when this type is selected.
export const ACCOUNT_TYPES = [
  { value: 0, label: 'Seleccionar...' },
  { value: 1, label: 'Corriente' },
  { value: 2, label: 'Detracción' },
]

// BankEntity.BancoNacion = 6 (used by the TaxInfoSection auto-lock logic).
export const BANCO_NACION_ID = 6

// ── IncomeTaxCategory (MineTrace.Shared.Enums.IncomeTaxCategory) ──
export const INCOME_TAX_CATEGORIES = [
  { value: 0, label: 'Seleccionar...' },
  { value: 1, label: 'Primera Categoria' },
  { value: 2, label: 'Segunda Categoria' },
  { value: 3, label: 'Tercera Categoria' },
  { value: 4, label: 'Cuarta Categoria' },
  { value: 5, label: 'Quinta Categoria' },
]

// ── Mining Right Statuses (used in Derechos Mineros) ──
export const MINING_RIGHT_STATUSES = [
  { value: 1, label: 'Vigente' },
  { value: 2, label: 'Extinto' },
  { value: 3, label: 'En Trámite' },
  { value: 4, label: 'Suspendido' },
]

// ── ScheduleStatus (MineTrace.Shared.Enums.ScheduleStatus) ──
export const SCHEDULE_STATUSES = [
  { value: 0, label: 'Borrador' },
  { value: 1, label: 'Programado' },
  { value: 2, label: 'En Proceso' },
  { value: 3, label: 'Completado' },
  { value: 4, label: 'Finalizado' },
]

// ── DetailStatus (MineTrace.Shared.Enums.DetailStatus) ──
export const DETAIL_STATUSES = [
  { value: 1, label: 'Listo' },
  { value: 2, label: 'En Proceso' },
  { value: 3, label: 'Completado' },
  { value: 4, label: 'Finalizado' },
  { value: 5, label: 'Pausado' },
  { value: 6, label: 'Aprobada' },
  { value: 7, label: 'Rechazada' },
]
